const EARTH_RADIUS_METERS = 6371008.8
const DEFAULT_CIRCLE_STEPS = 64

export const CIRCLE_UNITS = [
  { value: "nm", label: "Nautical miles", meters: 1852 },
  { value: "sm", label: "Statute miles", meters: 1609.344 },
  { value: "km", label: "Kilometers", meters: 1000 },
]

export const ARC_DIRECTIONS = [
  { value: "cw", label: "Clockwise" },
  { value: "ccw", label: "Counter-clockwise" },
]

const CIRCLE_UNIT_TO_METERS = Object.fromEntries(CIRCLE_UNITS.map((unit) => [unit.value, unit.meters]))
const ARC_DIRECTION_VALUES = new Set(ARC_DIRECTIONS.map((direction) => direction.value))

export function normalizeCircleUnit(unit) {
  const normalized = String(unit || "").trim().toLowerCase()
  return CIRCLE_UNIT_TO_METERS[normalized] ? normalized : "nm"
}

export function circleRadiusToMeters(radius, unit) {
  const numericRadius = Number(radius)
  if (!Number.isFinite(numericRadius) || numericRadius <= 0) return NaN
  return numericRadius * CIRCLE_UNIT_TO_METERS[normalizeCircleUnit(unit)]
}

export function normalizeArcDirection(direction) {
  const normalized = String(direction || "").trim().toLowerCase()
  return ARC_DIRECTION_VALUES.has(normalized) ? normalized : "cw"
}

function toRadians(value) {
  return (Number(value) * Math.PI) / 180
}

function toDegrees(value) {
  return (Number(value) * 180) / Math.PI
}

function normalizeLongitude(lng) {
  return ((((lng + 180) % 360) + 360) % 360) - 180
}

export function normalizeBearing(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return NaN
  return ((numeric % 360) + 360) % 360
}

export function destinationPoint(centerLng, centerLat, bearingDegrees, distanceMeters) {
  const angularDistance = Number(distanceMeters) / EARTH_RADIUS_METERS
  const bearing = toRadians(bearingDegrees)
  const lat1 = toRadians(centerLat)
  const lng1 = toRadians(centerLng)

  const sinLat1 = Math.sin(lat1)
  const cosLat1 = Math.cos(lat1)
  const sinAd = Math.sin(angularDistance)
  const cosAd = Math.cos(angularDistance)

  const lat2 = Math.asin(sinLat1 * cosAd + cosLat1 * sinAd * Math.cos(bearing))
  const lng2 = lng1 + Math.atan2(Math.sin(bearing) * sinAd * cosLat1, cosAd - sinLat1 * Math.sin(lat2))

  return [normalizeLongitude(toDegrees(lng2)), toDegrees(lat2)]
}

function validateCenterAndRadius({ centerLng, centerLat, radius, radiusUnit }) {
  const lng = Number(centerLng)
  const lat = Number(centerLat)
  const distanceMeters = circleRadiusToMeters(radius, radiusUnit)

  if (!Number.isFinite(lat) || lat < -90 || lat > 90) return { error: "Center latitude must be between -90 and 90." }
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) return { error: "Center longitude must be between -180 and 180." }
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) return { error: "Radius must be greater than 0." }

  return { lng, lat, distanceMeters }
}

export function buildCircleRing({ centerLng, centerLat, radius, radiusUnit = "nm", steps = DEFAULT_CIRCLE_STEPS }) {
  const validated = validateCenterAndRadius({ centerLng, centerLat, radius, radiusUnit })
  if (validated.error) return validated

  const { lng, lat, distanceMeters } = validated
  const segmentCount = Math.max(16, Math.round(Number(steps) || DEFAULT_CIRCLE_STEPS))

  const ring = []
  for (let i = 0; i < segmentCount; i++) {
    ring.push(destinationPoint(lng, lat, (360 * i) / segmentCount, distanceMeters))
  }
  ring.push(ring[0])

  return {
    ring,
    radiusMeters: distanceMeters,
    steps: segmentCount,
  }
}

export function buildCircleGeometry(options) {
  const built = buildCircleRing(options)
  if (built.error) return built
  return {
    geometry: { type: "Polygon", coordinates: [built.ring] },
    radiusMeters: built.radiusMeters,
    steps: built.steps,
  }
}

export function computeArcSweep(startBearing, endBearing, direction = "cw") {
  const start = normalizeBearing(startBearing)
  const end = normalizeBearing(endBearing)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return NaN

  // When start === end the raw modulo yields 0 — treat that as a full 360°.
  const raw = normalizeArcDirection(direction) === "ccw"
    ? ((start - end) % 360 + 360) % 360
    : ((end - start) % 360 + 360) % 360
  return raw === 0 ? 360 : raw
}

export function buildArcCoordinates({
  centerLng,
  centerLat,
  radius,
  radiusUnit = "nm",
  startBearing,
  endBearing,
  direction = "cw",
  steps,
}) {
  const validated = validateCenterAndRadius({ centerLng, centerLat, radius, radiusUnit })
  if (validated.error) return validated

  const normalizedStart = normalizeBearing(startBearing)
  const normalizedEnd = normalizeBearing(endBearing)
  if (!Number.isFinite(normalizedStart) || !Number.isFinite(normalizedEnd)) return { error: "Arc bearings must be valid numbers." }

  const normalizedDirection = normalizeArcDirection(direction)
  const sweepDegrees = computeArcSweep(normalizedStart, normalizedEnd, normalizedDirection)
  if (!Number.isFinite(sweepDegrees) || sweepDegrees <= 0) return { error: "Arc sweep must be greater than 0 degrees." }

  const segmentCount = Math.max(4, Math.round(Number(steps) || Math.ceil((sweepDegrees / 360) * DEFAULT_CIRCLE_STEPS)))
  const coordinates = []
  for (let i = 0; i <= segmentCount; i++) {
    const progress = i / segmentCount
    const bearing = normalizedDirection === "ccw"
      ? normalizedStart - sweepDegrees * progress
      : normalizedStart + sweepDegrees * progress
    coordinates.push(destinationPoint(validated.lng, validated.lat, bearing, validated.distanceMeters))
  }

  return {
    coordinates,
    radiusMeters: validated.distanceMeters,
    startBearing: normalizedStart,
    endBearing: normalizedEnd,
    direction: normalizedDirection,
    sweepDegrees,
    steps: segmentCount,
  }
}

export function buildArcGeometry(options) {
  const built = buildArcCoordinates(options)
  if (built.error) return built
  return {
    geometry: { type: "LineString", coordinates: built.coordinates },
    radiusMeters: built.radiusMeters,
    startBearing: built.startBearing,
    endBearing: built.endBearing,
    direction: built.direction,
    sweepDegrees: built.sweepDegrees,
    steps: built.steps,
  }
}

export function buildSectorGeometry(options) {
  const built = buildArcCoordinates(options)
  if (built.error) return built

  const centerLng = Number(options?.centerLng)
  const centerLat = Number(options?.centerLat)
  const ring = [[centerLng, centerLat], ...built.coordinates, [centerLng, centerLat]]

  return {
    geometry: { type: "Polygon", coordinates: [ring] },
    radiusMeters: built.radiusMeters,
    startBearing: built.startBearing,
    endBearing: built.endBearing,
    direction: built.direction,
    sweepDegrees: built.sweepDegrees,
    steps: built.steps,
  }
}

export function buildCircleFeature({
  id,
  name,
  description,
  color,
  opacity,
  centerLng,
  centerLat,
  radius,
  radiusUnit = "nm",
  steps = DEFAULT_CIRCLE_STEPS,
}) {
  const built = buildCircleGeometry({ centerLng, centerLat, radius, radiusUnit, steps })
  if (built.error) return built

  return {
    type: "Feature",
    id,
    properties: {
      featureType: "circle",
      name: String(name || "Circle").trim() || "Circle",
      description: String(description || ""),
      color,
      opacity: Math.min(1, Math.max(0, Number(opacity ?? 0.25))),
      centerLat: Number(centerLat),
      centerLng: Number(centerLng),
      radius: Number(radius),
      radiusUnit: normalizeCircleUnit(radiusUnit),
      radiusMeters: built.radiusMeters,
    },
    geometry: built.geometry,
  }
}

export function buildArcFeature({
  id,
  name,
  description,
  color,
  centerLng,
  centerLat,
  radius,
  radiusUnit = "nm",
  startBearing,
  endBearing,
  direction = "cw",
  steps,
}) {
  const built = buildArcGeometry({ centerLng, centerLat, radius, radiusUnit, startBearing, endBearing, direction, steps })
  if (built.error) return built

  return {
    type: "Feature",
    id,
    properties: {
      featureType: "arc",
      name: String(name || "Arc").trim() || "Arc",
      description: String(description || ""),
      color,
      centerLat: Number(centerLat),
      centerLng: Number(centerLng),
      radius: Number(radius),
      radiusUnit: normalizeCircleUnit(radiusUnit),
      radiusMeters: built.radiusMeters,
      startBearing: built.startBearing,
      endBearing: built.endBearing,
      arcDirection: built.direction,
      arcSweepDegrees: built.sweepDegrees,
    },
    geometry: built.geometry,
  }
}

export function buildSectorFeature({
  id,
  name,
  description,
  color,
  opacity,
  centerLng,
  centerLat,
  radius,
  radiusUnit = "nm",
  startBearing,
  endBearing,
  direction = "cw",
  steps,
}) {
  const built = buildSectorGeometry({ centerLng, centerLat, radius, radiusUnit, startBearing, endBearing, direction, steps })
  if (built.error) return built

  return {
    type: "Feature",
    id,
    properties: {
      featureType: "sector",
      name: String(name || "Sector").trim() || "Sector",
      description: String(description || ""),
      color,
      opacity: Math.min(1, Math.max(0, Number(opacity ?? 0.25))),
      centerLat: Number(centerLat),
      centerLng: Number(centerLng),
      radius: Number(radius),
      radiusUnit: normalizeCircleUnit(radiusUnit),
      radiusMeters: built.radiusMeters,
      startBearing: built.startBearing,
      endBearing: built.endBearing,
      arcDirection: built.direction,
      arcSweepDegrees: built.sweepDegrees,
    },
    geometry: built.geometry,
  }
}
